import { useEffect, useState } from 'react'
import { getCookie } from 'cookies-next'
import { useRegionStore } from '../store/regionStore'
import usePathnameCompat from './usePathnameCompat'
import {
  DEFAULT_REGION,
  REGION_COOKIE,
  isRegion,
  stripRegionFromPath,
  countryToRegion,
} from './regions'

/**
 * Resolve the current region as seen by the NAV (works on content pages too,
 * where the URL has no region prefix).
 *
 * Resolution order (matches the SSR resolveRegion intent):
 *   1. leading region segment of the current path  (deterministic, SSR-safe)
 *   2. the REGION cookie                            (sticky choice)
 *   3. countryToRegion(state.newRegion)             (selected shipping country)
 *   4. DEFAULT_REGION ('us')
 *
 * HYDRATION SAFETY: the cookie and overmind state are NOT reliably identical on
 * the server and the client's first paint (the cookie is read from
 * document.cookie in the browser; the country state can be hydrated/mutated
 * client-side). So the FIRST render returns ONLY the URL-derived region (or
 * DEFAULT_REGION) — a value the server also computes from the same asPath — and
 * the cookie/state fallbacks are applied AFTER mount, where a client-only
 * update is allowed. This guarantees server HTML === client first render.
 */
export function useCurrentRegion() {
  const asPath = usePathnameCompat()
  // The selected/geo-resolved pricing country now lives on the Zustand region
  // store (Phase C2 part 2); it is SSR-empty (null) so the SSR + first client
  // render both fall through to the URL/DEFAULT region, and the post-mount value
  // refines the cookie-less content-page fallback below.
  const storeNewRegion = useRegionStore((s) => s.newRegion)

  const { region: urlRegion } = stripRegionFromPath(asPath)

  // Deterministic value used for SSR + first client render.
  const ssrRegion = isRegion(urlRegion) ? urlRegion : DEFAULT_REGION

  const [region, setRegion] = useState(ssrRegion)

  useEffect(() => {
    // If the URL already carries a region, it wins — keep it.
    if (isRegion(urlRegion)) {
      setRegion(urlRegion)
      return
    }
    // No region in the URL (content page): fall back to cookie -> country -> us.
    const cookieRegion = getCookie(REGION_COOKIE)
    if (isRegion(cookieRegion)) {
      setRegion(cookieRegion)
      return
    }
    if (storeNewRegion) {
      setRegion(countryToRegion(storeNewRegion))
      return
    }
    setRegion(DEFAULT_REGION)
  }, [urlRegion, storeNewRegion])

  return region
}

/**
 * The region the CURRENT PAGE is rendered for, derived ONLY from the URL prefix
 * — `null` on a non-region/content page (FAQ, blog, legal, downloads, …).
 *
 * Use this for building link HREFs: a commerce link should carry the page's
 * region on a region page (/eu/shop), and stay BARE on a content page (/shop)
 * so the server's bare-URL geo-IP redirect (cookie -> geo-IP -> us) picks the
 * visitor's region at click time. This differs from useCurrentRegion(), which
 * always RESOLVES to a concrete region (for the geo banner / region switcher).
 *
 * Purely URL-derived (no state/effect), so SSR === first client render and the
 * href never changes post-mount.
 */
export function useUrlRegion() {
  const asPath = usePathnameCompat()
  const { region } = stripRegionFromPath(asPath)
  return isRegion(region) ? region : null
}
